<script setup lang="ts">
import { Enums } from "@/enums";
import { formatRupiah } from "../../common/formats.common";
import type { ProvisionItemStatus } from "../../common/types.common";
import { defineActions, defineState } from "../common";
import { FormInputNumeric, FormInputSelect } from "../input";
import { FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = ProvisionItemStatus[];
interface PropsType extends BaseProps<ModelType> {
    hasValue?: () => boolean;
    excludeValues?: Set<Enums.ProvisionItemStatusEnum.ValuesClient>;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
});

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: props.hasValue ?? (() => model.value !== undefined),
});

const actions = defineActions(model, props, state);
</script>

<template>
    <div class="flex flex-col gap-2 overflow-x-hidden">
        <div v-if="props.embedMode !== 'inline'" class="flex flex-row items-center px-1 justify-between mr-2">
            <label class="text-gray-600 font-semibold">
                {{ props.label ?? "Penentuan Status dan Harga / Biaya Barang" }}
            </label>
            <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
        </div>

        <div class="w-full overflow-x-auto">
            <table class="rns-items-table">
                <thead>
                    <tr>
                        <th>Nama</th>
                        <th>No. Seri</th>
                        <th>Jumlah</th>
                        <th>Status</th>
                        <th>Harga / Biaya</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="model?.length === 0">
                        <td colspan="5">Tidak ada barang yang digunakan.</td>
                    </tr>
                    <tr v-for="item in model">
                        <td class="w-4/12">{{ item.name }}</td>
                        <td class="w-2/12">{{ item.code ?? "-" }}</td>
                        <td class="w-1/12">{{ item.quantity }}</td>
                        <td class="w-2/12" :class="{ 'text-start!': state.editMode }">
                            <FormInputSelect
                                v-if="state.editMode"
                                v-model="item.status"
                                embedMode="inline"
                                :mode="props.mode"
                                :options="
                                    Enums.ProvisionItemStatusEnum.selectClient.filter(
                                        (v) => !(excludeValues?.has(v.value) ?? false),
                                    )
                                "
                                placeholder="Pilih Status"
                                value="L"
                            />
                            <template v-else>
                                {{ Enums.ProvisionItemStatusEnum.label(item.status) }}
                            </template>
                        </td>
                        <td class="w-3/12">
                            <FormInputNumeric
                                v-if="state.editMode && item.status === 'R'"
                                v-model="item.price"
                                class="min-w-52"
                                embedMode="inline"
                                label="Harga Sewa"
                                :mode="props.mode"
                                prefix="Rp. "
                                suffix=" / bulan"
                            />
                            <FormInputNumeric
                                v-else-if="state.editMode && item.status === 'B'"
                                v-model="item.price"
                                class="min-w-52"
                                embedMode="inline"
                                label="Harga Jual"
                                :mode="props.mode"
                                prefix="Rp. "
                            />
                            <template v-else>
                                {{ formatRupiah(item.price) }}
                                <template v-if="item.status === 'R'">/ bulan</template>
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-items-table {
    @apply w-full;
}

.rns-items-table th {
    @apply border text-white bg-gray-800 py-2;
}

.rns-items-table td {
    @apply border p-2 text-center;
}
</style>
